import {
    ProjectionDefinition,
    ProjectionLog,
    ProjectionRunner,
} from "./projectionEngine.js";
import { LogSearchProjectionProvider } from "./logSearchProjectionProvider.js";

const CHANNEL_EVENT_TYPES = [
    "voice_join",
    "voice_leave",
    "voice_move",
    "member_move",
    "member_disconnect",
    "utils_voice_join",
    "utils_voice_move",
] as const;

const USER_STATE_EVENT_TYPES = [
    "voice_state_update",
    "voice_state_video",
    "voice_state_stream",
] as const;

const SNAPSHOT_REPLAY_MARGIN_MS = 5_000;

type NullableBoolean = boolean | null;

export type VoiceSnapshotState = {
    selfMute: NullableBoolean;
    selfDeaf: NullableBoolean;
    serverMute: NullableBoolean;
    serverDeaf: NullableBoolean;
    streaming: NullableBoolean;
    video: NullableBoolean;
};

export type VoiceSession = {
    userId: string;
    joinAt: number;
    leaveAt: number;
};

export type VoiceSnapshotMember = VoiceSession & {
    selected: boolean;
    state: VoiceSnapshotState;
};

type VoiceSnapshotContext = {
    log: ProjectionLog;
    channelId: string;
    sessions: VoiceSession[];
    selectedUserId: string | null;
    userIds: string[];
};

type VoiceSnapshotProjectionState = {
    members: Map<string, VoiceSnapshotMember>;
};

export type VoiceChannelSnapshot = {
    channelId: string | null;
    selectedUserId: string | null;
    partial: boolean;
    eventCount: number;
    members: VoiceSnapshotMember[];
    membersPayload: Array<[ string, { id: string } | null ]>;
    logMembers: Array<{ id: string }>;
    logChannels: Array<{ id: string }>;
};

type VoiceInteractionsResponse = {
    error?: string;
    members?: Array<[ string, { id: string } | null ]>;
    result?: VoiceSession[];
};

type VoiceSnapshotFetcher = (url: string, options?: RequestInit) => Promise<{
    json(): Promise<VoiceInteractionsResponse>;
}>;

function quoteDslString(value: string): string {
    return `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, "\\\"")}"`;
}

function dslSet(values: readonly string[]): string {
    return `{ ${values.map(quoteDslString).join(", ")} }`;
}

function readVoiceChannelId(log: ProjectionLog): string | null {
    return log.channel || log.to || log.from || null;
}

function readVoiceActorId(log: ProjectionLog): string | null {
    return log.target || log.executor || null;
}

function hasOwn(object: Record<string, unknown> | undefined, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(object ?? {}, key);
}

function createUnknownVoiceState(): VoiceSnapshotState {
    return {
        selfMute    : null,
        selfDeaf    : null,
        serverMute  : null,
        serverDeaf  : null,
        streaming   : null,
        video       : null,
    };
}

function createSessionState(session: VoiceSession, context: VoiceSnapshotContext): VoiceSnapshotMember {
    return {
        userId      : session.userId,
        joinAt      : Number(session.joinAt),
        leaveAt     : Number(session.leaveAt),
        selected    : session.userId === context.selectedUserId,
        state       : createUnknownVoiceState(),
    };
}

function applyBooleanState(target: VoiceSnapshotState, extra: Record<string, unknown> | undefined, key: keyof VoiceSnapshotState): void {
    if (hasOwn(extra, key))
        target[key] = Boolean(extra?.[key]);
}

function applyVoiceStateUpdate(target: VoiceSnapshotMember, log: ProjectionLog): void {
    applyBooleanState(target.state, log.extra, "selfMute");
    applyBooleanState(target.state, log.extra, "selfDeaf");
    applyBooleanState(target.state, log.extra, "serverMute");
    applyBooleanState(target.state, log.extra, "serverDeaf");
}

function applyVoiceStreamUpdate(target: VoiceSnapshotMember, log: ProjectionLog): void {
    applyBooleanState(target.state, log.extra, "streaming");
}

function applyVoiceVideoUpdate(target: VoiceSnapshotMember, log: ProjectionLog): void {
    applyBooleanState(target.state, log.extra, "video");
}

function createVoiceChannelQueries(context: VoiceSnapshotContext) {
    const queries = [];

    if (context.channelId)
        queries.push({
            name        : "voice-channel-events",
            expression  : [
                `type in ${dslSet(CHANNEL_EVENT_TYPES)}`,
                `and (channel == ${quoteDslString(context.channelId)} or from == ${quoteDslString(context.channelId)} or to == ${quoteDslString(context.channelId)})`,
            ].join(" "),
        });

    if (context.userIds.length)
        queries.push({
            name        : "voice-user-state-events",
            expression  : [
                `type in ${dslSet(USER_STATE_EVENT_TYPES)}`,
                `and executor in ${dslSet(context.userIds)}`,
            ].join(" "),
        });

    return queries;
}

export const VoiceChannelSnapshotProjection: ProjectionDefinition<
    VoiceSnapshotProjectionState,
    VoiceSnapshotContext,
    Omit<VoiceChannelSnapshot, "membersPayload" | "logMembers" | "logChannels">
> = {
    queries: createVoiceChannelQueries,

    createState(context) {
        return {
            members: new Map(context.sessions.map((session) => [
                session.userId,
                createSessionState(session, context),
            ])),
        };
    },

    apply(state, log) {
        const userId = log.executor || log.target;
        const target = userId
            ? state.members.get(userId)
            : null;

        if (!target)
            return;

        switch (log.type) {
            case "voice_state_update":
                applyVoiceStateUpdate(target, log);
                return;

            case "voice_state_stream":
                applyVoiceStreamUpdate(target, log);
                return;

            case "voice_state_video":
                applyVoiceVideoUpdate(target, log);
                return;

            default:
                return;
        }
    },

    finalize(state, context, meta) {
        return {
            channelId       : context.channelId,
            selectedUserId  : context.selectedUserId,
            partial         : meta.partial,
            eventCount      : meta.eventCount,
            members         : [ ...state.members.values() ].sort((left, right) => left.joinAt - right.joinAt),
        };
    },
};

export class VoiceChannelSnapshotService {
    readonly fetcher: VoiceSnapshotFetcher;
    readonly runner: ProjectionRunner;

    constructor({
        fetcher = (...args) => window.fetch(...args),
        runner = new ProjectionRunner({
            queryProvider: new LogSearchProjectionProvider(),
        }),
    }: {
        fetcher?: VoiceSnapshotFetcher;
        runner?: ProjectionRunner;
    } = {}) {
        this.fetcher = fetcher;
        this.runner = runner;
    }

    async fetch(log: ProjectionLog, { signal }: { signal?: AbortSignal } = {}): Promise<VoiceChannelSnapshot> {
        const channelId = readVoiceChannelId(log);

        if (!channelId)
            return this.createEmptySnapshot(log, channelId);

        const sessionsResult = await this.fetchVoiceSessions(log, channelId, signal);
        const sessions = this.normalizeSessions(sessionsResult.result);
        const context = this.createProjectionContext(log, channelId, sessions);

        if (!sessions.length)
            return {
                ...this.createEmptySnapshot(log, channelId),
                membersPayload: sessionsResult.members ?? [],
            };

        let projectionResult;

        try {
            projectionResult = await this.runner.run({
                projection: VoiceChannelSnapshotProjection,
                context,
                window: {
                    from    : Math.max(0, Math.min(...sessions.map((session) => session.joinAt)) - SNAPSHOT_REPLAY_MARGIN_MS),
                    to      : log.createdAt + 2,
                    at      : log.createdAt,
                },
                signal,
            });

        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError")
                throw error;

            projectionResult = {
                result: VoiceChannelSnapshotProjection.finalize(
                    VoiceChannelSnapshotProjection.createState(context),
                    context,
                    {
                        partial     : true,
                        errors      : [],
                        eventCount  : 0,
                        members     : [],
                        channels    : [],
                    },
                ),
                members     : [],
                channels    : [],
            };
        }

        return {
            ...projectionResult.result,
            membersPayload  : sessionsResult.members ?? [],
            logMembers      : projectionResult.members,
            logChannels     : projectionResult.channels,
        };
    }

    async fetchVoiceSessions(log: ProjectionLog, channelId: string, signal?: AbortSignal): Promise<VoiceInteractionsResponse> {
        const params = new URLSearchParams({
            channelId,
            after   : String(log.createdAt - 1),
            before  : String(log.createdAt + 1),
        });
        const response = await this.fetcher(`/api/voice-interactions?${params.toString()}`, { signal });
        const result = await response.json();

        if (result.error)
            throw new Error(result.error);

        return result;
    }

    normalizeSessions(sessions: VoiceSession[] = []): VoiceSession[] {
        return sessions
            .map((session) => ({
                userId  : String(session.userId),
                joinAt  : Number(session.joinAt),
                leaveAt : Number(session.leaveAt),
            }))
            .sort((left, right) => left.joinAt - right.joinAt);
    }

    createProjectionContext(log: ProjectionLog, channelId: string, sessions: VoiceSession[]): VoiceSnapshotContext {
        return {
            log,
            channelId,
            sessions,
            selectedUserId  : readVoiceActorId(log),
            userIds         : sessions.map((session) => session.userId),
        };
    }

    createEmptySnapshot(log: ProjectionLog, channelId: string | null): VoiceChannelSnapshot {
        return {
            channelId,
            selectedUserId  : readVoiceActorId(log),
            partial         : false,
            eventCount      : 0,
            members         : [],
            membersPayload  : [],
            logMembers      : [],
            logChannels     : [],
        };
    }
}
